/** 用户 API（对接 /system/user/*） */

import request from './request';
import type { LoginResult, UserInfoResult, User, PageResult, RoleOption } from '@bluplanet/shared';

export interface LoginParams {
  username: string;
  password: string;
}

export interface UserQuery {
  page?: number;
  pageSize?: number;
  username?: string;
}

/** 用户表单（新增/编辑共用；password 仅新增时传） */
export interface UserForm {
  id?: string;
  username: string;
  password?: string;
  realName?: string;
  email?: string;
  phone?: string;
  status?: number;
  roles?: string[]; // 角色编码集合（roleKey），后端 assignRoles 按 roleKey 处理
}

/** 当前登录用户完整档案（/system/user/info 的 user 字段 = SysUser 实体） */
export interface ProfileDetail {
  id: string;
  username: string;
  nickName?: string;
  realName?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  /** 性别：0-未知, 1-男, 2-女 */
  sex?: string;
  status?: number;
  orgId?: string;
  /**
   * 最后登录 IP / 时间。注意：后端登录链路全仓无 setLoginIp/setLoginDate 写入点，
   * 库里恒为 NULL，前端不展示这两项（个人设置页已移除该行）。字段保留以备后端补录后直接启用。
   */
  loginIp?: string;
  loginDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** /system/user/info 响应（含完整档案，比 UserInfoResult.user 更全） */
export interface ProfileResponse {
  user: ProfileDetail;
  roles?: string[];
  permissions?: string[];
  isSuperAdmin?: boolean;
  platform?: boolean;
}

/** 个人资料可改字段（与后端 updateProfile 白名单一致） */
export interface ProfileForm {
  nickName?: string;
  realName?: string;
  email?: string;
  phone?: string;
  sex?: string;
  avatar?: string;
}

/** 登录：POST /system/user/login */
export function login(params: LoginParams) {
  return request<LoginResult>({ url: '/system/user/login', method: 'POST', data: params });
}

/** 获取当前用户信息（菜单/权限/角色）：GET /system/user/info */
export function getInfo() {
  return request<UserInfoResult>({ url: '/system/user/info', method: 'GET' });
}

/** 获取当前用户完整档案（复用 /system/user/info，字段比 getInfo 的 user 更全）：GET /system/user/info */
export function getProfile() {
  return request<ProfileResponse>({ url: '/system/user/info', method: 'GET' });
}

/** 更新个人资料：PUT /system/user/profile（仅本人，无权限码，登录即可） */
export function updateProfile(form: ProfileForm) {
  return request<boolean>({ url: '/system/user/profile', method: 'PUT', data: form });
}

/** 修改本人密码：PUT /system/user/changePassword */
export function changePassword(oldPassword: string, newPassword: string) {
  return request<boolean>({
    url: '/system/user/changePassword',
    method: 'PUT',
    data: { oldPassword, newPassword },
  });
}

/** 用户列表（分页）：GET /system/user/list */
export function listUsers(params: UserQuery) {
  return request<PageResult<User>>({ url: '/system/user/list', method: 'GET', params });
}

/** 用户详情：GET /system/user/{id} */
export function getUser(id: string) {
  return request<User>({ url: `/system/user/${id}`, method: 'GET' });
}

/** 新增用户：POST /system/user */
export function createUser(form: UserForm) {
  return request<boolean>({ url: '/system/user', method: 'POST', data: form });
}

/** 编辑用户：PUT /system/user */
export function updateUser(form: UserForm) {
  return request<boolean>({ url: '/system/user', method: 'PUT', data: form });
}

/** 删除用户（逻辑删除）：DELETE /system/user/{ids}（ids 逗号拼接） */
export function deleteUsers(ids: string[]) {
  return request<boolean>({ url: `/system/user/${ids.join(',')}`, method: 'DELETE' });
}

/** 重置密码：PUT /system/user/resetPassword */
export function resetPassword(userId: string, newPassword?: string) {
  return request<boolean>({
    url: '/system/user/resetPassword',
    method: 'PUT',
    data: { userId, newPassword },
  });
}

/** 角色下拉（全部，用于用户表单多选）：GET /system/role/list（不传分页返回全量） */
export function listRoleOptions() {
  return request<RoleOption[]>({ url: '/system/role/list', method: 'GET' });
}

/** 聚合对象：页面按 userApi.xxx 调用 */
export const userApi = {
  login,
  getInfo,
  getProfile,
  updateProfile,
  changePassword,
  listUsers,
  getUser,
  createUser,
  updateUser,
  deleteUsers,
  resetPassword,
  listRoleOptions,
};
